import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link, Element, animateScroll as scroll } from 'react-scroll';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import CountUp from 'react-countup';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  Globe, 
  Wallet, 
  Shield, 
  Headphones,
  ArrowRight,
  ArrowDown,
  CheckCircle2,
  TrendingUp,
  Zap,
  CreditCard,
  Send,
  PiggyBank,
  Building2,
  Users,
  Clock,
  Lock,
  Star,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Play,
  Quote,
  Briefcase,
  Leaf
} from 'lucide-react';

// Hero Background Slider - 5 premium images with unique transitions
const heroSlides = [
  'https://images.unsplash.com/photo-1758691737492-48e8fdd336f7?auto=format&fit=crop&w=1920&q=80',
  'https://images.unsplash.com/photo-1758691737538-220c1902b1ca?auto=format&fit=crop&w=1920&q=80',
  'https://images.pexels.com/photos/7644148/pexels-photo-7644148.jpeg?auto=compress&cs=tinysrgb&w=1920',
  'https://images.unsplash.com/photo-1758691737584-a8f17fb34475?auto=format&fit=crop&w=1920&q=80',
  'https://images.unsplash.com/photo-1586494061274-512ab3296f84?auto=format&fit=crop&w=1920&q=80',
];

const slideTransitions = [
  { initial: { opacity: 0, scale: 1.15 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 0.95 }, transition: { duration: 1.8, ease: 'easeInOut' } },
  { initial: { opacity: 0, x: '8%' }, animate: { opacity: 1, x: '0%' }, exit: { opacity: 0, x: '-8%' }, transition: { duration: 1.5, ease: [0.25, 0.46, 0.45, 0.94] } },
  { initial: { opacity: 0, y: '6%' }, animate: { opacity: 1, y: '0%' }, exit: { opacity: 0, y: '-6%' }, transition: { duration: 1.6, ease: 'easeOut' } },
  { initial: { opacity: 0, scale: 0.92, rotate: 1 }, animate: { opacity: 1, scale: 1, rotate: 0 }, exit: { opacity: 0, scale: 1.08, rotate: -1 }, transition: { duration: 1.7, ease: 'easeInOut' } },
  { initial: { opacity: 0, x: '-6%', scale: 1.05 }, animate: { opacity: 1, x: '0%', scale: 1 }, exit: { opacity: 0, x: '6%', scale: 0.95 }, transition: { duration: 1.5, ease: [0.4, 0, 0.2, 1] } },
];

const HeroBackgroundSlider = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent(prev => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const t = slideTransitions[current];

  return (
    <div className="absolute inset-0 z-[1]" data-testid="hero-slider">
      <AnimatePresence mode="wait">
        <motion.div
          key={current}
          initial={t.initial}
          animate={t.animate}
          exit={t.exit}
          transition={t.transition}
          className="absolute inset-0"
        >
          <img
            src={heroSlides[current]}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: 0.3, filter: 'brightness(0.55) saturate(0.75)' }}
          />
        </motion.div>
      </AnimatePresence>
      {/* Slide indicator dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[4] flex gap-2">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className="transition-all duration-500"
            style={{
              width: i === current ? '28px' : '8px',
              height: '8px',
              borderRadius: '4px',
              background: i === current ? 'rgba(6,182,212,0.9)' : 'rgba(255,255,255,0.25)',
              border: 'none',
              cursor: 'pointer',
            }}
          />
        ))}
      </div>
    </div>
  );
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

// Animated Section Wrapper
const AnimatedSection = ({ children, className = "" }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Counter Component
const AnimatedCounter = ({ end, suffix = "", prefix = "", decimals = 0 }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true });
  
  return (
    <span ref={ref}>
      {isInView ? (
        <CountUp end={end} suffix={suffix} prefix={prefix} decimals={decimals} duration={2.5} />
      ) : (
        `${prefix}0${suffix}`
      )}
    </span>
  );
};

const LandingPage = () => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', to: 'hero' },
    { name: 'About', to: 'about' },
    { name: 'Services', to: 'services' },
    { name: 'Features', to: 'features' },
    { name: 'How It Works', to: 'how-it-works' },
    { name: 'Testimonials', to: 'testimonials' },
  ];

  const productItems = [
    { name: 'Moringa Agro Invest', to: '/moringa-invest', icon: Leaf, green: true },
    { name: 'Personal Accounts', to: '/signup', icon: Users },
    { name: 'Business Accounts', to: '/signup', icon: Building2 },
    { name: 'International Transfers', to: '/transfer-info', icon: Send },
    { name: 'Crypto Deposits', to: '/deposit', icon: Wallet },
    { name: 'Commercial Officer Program', to: '/co-application', icon: Briefcase, featured: true },
  ];

  const services = [
    {
      icon: Send,
      title: 'International Transfers',
      description: 'Send money to 180+ countries with competitive exchange rates and low fees.',
      color: 'bg-blue-500'
    },
    {
      icon: Wallet,
      title: 'Multi-Currency Accounts',
      description: 'Hold and manage multiple currencies in one account. Convert instantly.',
      color: 'bg-emerald-500'
    },
    {
      icon: CreditCard,
      title: 'Virtual & Physical Cards',
      description: 'Get a card that works worldwide with no foreign transaction fees.',
      color: 'bg-purple-500'
    },
    {
      icon: PiggyBank,
      title: 'Savings & Investments',
      description: 'Grow your wealth with competitive interest rates and smart investment tools.',
      color: 'bg-amber-500'
    },
    {
      icon: Building2,
      title: 'Business Banking',
      description: 'Complete financial solutions for businesses of all sizes.',
      color: 'bg-rose-500'
    },
    {
      icon: Shield,
      title: 'Secure Payments',
      description: 'Bank-grade security with 2FA and real-time fraud protection.',
      color: 'bg-cyan-500'
    }
  ];

  const features = [
    { icon: Zap, title: 'Instant Transfers', desc: 'Money arrives in seconds, not days' },
    { icon: Lock, title: 'Bank-Grade Security', desc: '256-bit encryption & 2FA protection' },
    { icon: Globe, title: '180+ Countries', desc: 'Send money anywhere in the world' },
    { icon: Clock, title: '24/7 Support', desc: 'We\'re here whenever you need us' },
    { icon: TrendingUp, title: 'Best Rates', desc: 'Real exchange rates, no hidden fees' },
    { icon: Users, title: '2M+ Customers', desc: 'Trusted by millions worldwide' }
  ];

  const howItWorks = [
    { step: '01', title: 'Create Account', desc: 'Sign up in minutes with just your email and ID verification.' },
    { step: '02', title: 'Add Funds', desc: 'Deposit money via bank transfer, card, or other payment methods.' },
    { step: '03', title: 'Start Banking', desc: 'Send money, pay bills, invest, and manage your finances globally.' }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Entrepreneur',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face',
      text: 'Youse Capital transformed how I manage my international business payments. Fast, reliable, and incredibly cost-effective.'
    },
    {
      name: 'Michael Chen',
      role: 'Digital Nomad',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      text: 'As someone who travels constantly, having a multi-currency account has been a game-changer. No more crazy conversion fees!'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Freelancer',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      text: 'Getting paid by international clients used to be a nightmare. Now it\'s seamless. Best banking decision I ever made.'
    }
  ];

  const stats = [
    { value: 180, suffix: '+', label: 'Countries' },
    { value: 10, prefix: '$', suffix: 'B+', label: 'Transferred' },
    { value: 2, suffix: 'M+', label: 'Happy Customers' },
    { value: 99.9, suffix: '%', decimals: 1, label: 'Uptime' }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Sticky Navigation */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="hero" smooth={true} duration={800} className="cursor-pointer">
              <img 
                src="https://customer-assets.emergentagent.com/job_1d3a7cf2-25a3-4666-9f30-7291006a257d/artifacts/obfdqs4z_ChatGPT%20Image%2010%20de%20fev.%20de%202026%2C%2002_11_06.png" 
                alt="Youse Capital" 
                className="h-36 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  smooth={true}
                  duration={800}
                  offset={-80}
                  spy={true}
                  activeClass="text-primary font-semibold"
                  className={`cursor-pointer text-sm font-medium transition-colors hover:text-primary ${
                    scrolled ? 'text-slate-700' : 'text-white'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Products Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setProductsOpen(true)}
                onMouseLeave={() => setProductsOpen(false)}
              >
                <button
                  className={`flex items-center gap-1 cursor-pointer text-sm font-medium transition-colors hover:text-primary ${
                    scrolled ? 'text-slate-700' : 'text-white'
                  }`}
                  data-testid="products-menu-btn"
                >
                  Products
                  <ChevronDown className={`w-4 h-4 transition-transform ${productsOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {productsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-100 py-2 z-50"
                  >
                    {productItems.map((item) => (
                      <RouterLink
                        key={item.to}
                        to={item.to}
                        className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors hover:bg-slate-50 ${
                          item.green ? 'text-emerald-700 font-semibold bg-emerald-50/70'
                          : item.featured ? 'text-[#1a365d] font-semibold bg-blue-50/50' : 'text-slate-700'
                        }`}
                        data-testid={`products-menu-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <item.icon className={`w-5 h-5 ${item.green ? 'text-emerald-600' : item.featured ? 'text-[#1a365d]' : 'text-slate-400'}`} />
                        <span>{item.name}</span>
                        {item.green && (
                          <span className="ml-auto text-xs bg-emerald-600 text-white px-2 py-0.5 rounded-full">New</span>
                        )}
                        {item.featured && !item.green && (
                          <span className="ml-auto text-xs bg-[#1a365d] text-white px-2 py-0.5 rounded-full">New</span>
                        )}
                      </RouterLink>
                    ))}
                  </motion.div>
                )}
              </div>
            </div>

            {/* Right side */}
            <div className="hidden lg:flex items-center gap-4">
              <LanguageSelector />
              <RouterLink to="/login">
                <Button variant="ghost" className={scrolled ? 'text-slate-700' : 'text-white hover:bg-white/10'}>
                  {t('login')}
                </Button>
              </RouterLink>
              <RouterLink to={isAuthenticated ? '/dashboard' : '/signup'}>
                <Button className="bg-primary hover:bg-primary/90 gap-2" data-testid="nav-get-started">
                  {t('getStarted')}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </RouterLink>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className={scrolled ? 'text-slate-900' : 'text-white'} />
              ) : (
                <Menu className={scrolled ? 'text-slate-900' : 'text-white'} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t"
          >
            <div className="px-4 py-6 space-y-4">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  smooth={true}
                  duration={800}
                  offset={-80}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-slate-700 hover:text-primary font-medium py-2 cursor-pointer"
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Products Section in Mobile */}
              <div className="pt-4 border-t">
                <p className="text-sm font-semibold text-slate-500 mb-3">Products</p>
                {productItems.map((item) => (
                  <RouterLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 py-2 hover:text-primary ${
                      item.green ? 'font-semibold text-emerald-700'
                      : item.featured ? 'font-semibold text-[#1a365d]' : 'text-slate-700'
                    }`}
                  >
                    <item.icon className={`w-4 h-4 ${item.green ? 'text-emerald-600' : ''}`} />
                    {item.name}
                    {item.green && (
                      <span className="text-xs bg-emerald-600 text-white px-2 py-0.5 rounded-full">New</span>
                    )}
                    {item.featured && !item.green && (
                      <span className="text-xs bg-[#1a365d] text-white px-2 py-0.5 rounded-full">New</span>
                    )}
                  </RouterLink>
                ))}
              </div>
              
              <div className="pt-4 border-t flex flex-col gap-3">
                <RouterLink to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full">{t('login')}</Button>
                </RouterLink>
                <RouterLink to={isAuthenticated ? '/dashboard' : '/signup'} onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full">{t('getStarted')}</Button>
                </RouterLink>
              </div>
            </div>
          </motion.div>
        )}
      </motion.nav>

      {/* Hero Section */}
      <Element name="hero">
        <section className="relative min-h-screen flex items-center overflow-hidden bg-[#1a365d]" data-testid="hero-section">
          {/* Background Image Slider with 70% transparency */}
          <HeroBackgroundSlider />

          {/* Gradient overlay for text readability */}
          <div className="absolute inset-0 z-[2]">
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a365d]/90 via-[#1a365d]/75 to-[#1a365d]/50" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a365d] via-transparent to-[#1a365d]/60" />
          </div>

          {/* Animated accents on top */}
          <div className="absolute inset-0 z-[3] pointer-events-none">
            <motion.div
              animate={{ scale: [1, 1.2, 1], opacity: [0.08, 0.15, 0.08] }}
              transition={{ duration: 8, repeat: Infinity }}
              className="absolute top-1/4 -right-1/4 w-[800px] h-[800px] rounded-full bg-cyan-400/10 blur-3xl"
            />
            <motion.div
              animate={{ scale: [1, 1.3, 1], opacity: [0.06, 0.12, 0.06] }}
              transition={{ duration: 10, repeat: Infinity, delay: 1 }}
              className="absolute -bottom-1/4 -left-1/4 w-[600px] h-[600px] rounded-full bg-blue-400/10 blur-3xl"
            />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32" style={{ zIndex: 5 }}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <motion.span 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-cyan-300 text-sm font-medium mb-6"
                >
                  🚀 The Future of Digital Banking
                </motion.span>
                
                <motion.h1 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6"
                >
                  {t('heroTitle')}
                </motion.h1>
                
                <motion.p 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="text-lg sm:text-xl text-white/70 mb-8 max-w-xl"
                >
                  {t('heroSubtitle')}
                </motion.p>
                
                <motion.div 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                  className="flex flex-wrap gap-4"
                >
                  <RouterLink to={isAuthenticated ? '/dashboard' : '/signup'}>
                    <Button size="lg" className="bg-cyan-500 hover:bg-cyan-400 text-white gap-2 text-base px-8 py-6 rounded-xl" data-testid="hero-get-started">
                      {t('getStarted')}
                      <ArrowRight className="w-5 h-5" />
                    </Button>
                  </RouterLink>
                  <Link to="about" smooth={true} duration={800}>
                    <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 gap-2 text-base px-8 py-6 rounded-xl" data-testid="hero-learn-more">
                      {t('learnMore')}
                      <Play className="w-4 h-4" />
                    </Button>
                  </Link>
                </motion.div>

                {/* Trust badges */}
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="mt-12 flex items-center gap-6"
                >
                  <div className="flex -space-x-3">
                    {[1,2,3,4].map((i) => (
                      <div key={i} className="w-10 h-10 rounded-full bg-white/20 border-2 border-white/30 overflow-hidden">
                        <img 
                          src={`https://i.pravatar.cc/100?img=${i + 10}`} 
                          alt="Customer"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="text-white/80">
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map((i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-sm">Trusted by 2M+ customers</p>
                  </div>
                </motion.div>
              </motion.div>

              {/* Right Content - Card mockup */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="relative hidden lg:block"
              >
                <div className="relative">
                  {/* Main card */}
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    className="relative z-10"
                  >
                    <img 
                      src="https://customer-assets.emergentagent.com/job_1d3a7cf2-25a3-4666-9f30-7291006a257d/artifacts/1gghcyhn_ChatGPT%20Image%2010%20de%20fev.%20de%202026%2C%2002_20_21.png"
                      alt="Youse Capital App"
                      className="w-full max-w-md mx-auto rounded-3xl shadow-2xl"
                    />
                  </motion.div>

                  {/* Floating elements */}
                  <motion.div
                    animate={{ y: [0, 10, 0], x: [0, 5, 0] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-10 -left-10 bg-white rounded-2xl p-4 shadow-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Portfolio Growth</p>
                        <p className="text-lg font-bold text-green-600">+24.5%</p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    animate={{ y: [0, -15, 0], x: [0, -5, 0] }}
                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                    className="absolute bottom-20 -right-5 bg-white rounded-2xl p-4 shadow-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <Send className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Transfer Sent</p>
                        <p className="text-lg font-bold text-slate-900">$2,450.00</p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Scroll indicator */}
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
          >
            <Link to="about" smooth={true} duration={800} className="cursor-pointer">
              <div className="flex flex-col items-center text-white/50 hover:text-white transition-colors">
                <span className="text-sm mb-2">Scroll to explore</span>
                <ArrowDown className="w-5 h-5" />
              </div>
            </Link>
          </motion.div>
        </section>
      </Element>

      {/* Stats Section */}
      <section className="py-20 bg-slate-50" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-[#1a365d] mb-2">
                  <AnimatedCounter 
                    end={stat.value} 
                    prefix={stat.prefix || ''} 
                    suffix={stat.suffix || ''} 
                    decimals={stat.decimals || 0}
                  />
                </div>
                <div className="text-slate-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <Element name="about">
        <section className="py-24 bg-white" data-testid="about-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection>
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <motion.div variants={fadeInLeft}>
                  <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wider">Who We Are</span>
                  <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mt-4 mb-6 leading-tight">
                    Building the Future of Global Finance
                  </h2>
                  <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                    Youse Capital is a leading digital bank revolutionizing how people and businesses manage money globally. Founded with a vision to make international banking accessible to everyone, we've grown to serve over 2 million customers across 180+ countries.
                  </p>
                  <p className="text-slate-600 mb-8 leading-relaxed">
                    Our cutting-edge technology combined with bank-grade security ensures your money is always safe while being available whenever you need it. We believe in transparency, innovation, and putting our customers first.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-6 mb-8">
                    {[
                      { label: 'Founded', value: '2020' },
                      { label: 'Headquarters', value: 'New York, USA' },
                      { label: 'Team Size', value: '500+' },
                      { label: 'Funding', value: '$250M+' }
                    ].map((item, i) => (
                      <div key={i} className="border-l-2 border-cyan-500 pl-4">
                        <div className="text-sm text-slate-500">{item.label}</div>
                        <div className="text-xl font-bold text-slate-900">{item.value}</div>
                      </div>
                    ))}
                  </div>

                  <Link to="services" smooth={true} duration={800}>
                    <Button className="gap-2 bg-[#1a365d] hover:bg-[#1a365d]/90">
                      Explore Our Services
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </motion.div>

                <motion.div variants={fadeInRight} className="relative">
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                    <img 
                      src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=600&fit=crop"
                      alt="Youse Capital Team"
                      className="w-full h-[500px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a365d]/80 via-transparent to-transparent" />
                    <div className="absolute bottom-8 left-8 right-8 text-white">
                      <p className="text-lg font-semibold">Our Mission</p>
                      <p className="text-white/80">"Making global finance accessible, affordable, and instant for everyone."</p>
                    </div>
                  </div>
                  
                  {/* Floating badge */}
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 4, repeat: Infinity }}
                    className="absolute -top-6 -right-6 bg-white rounded-2xl p-6 shadow-xl"
                  >
                    <div className="flex items-center gap-3">
                      <Shield className="w-10 h-10 text-green-600" />
                      <div>
                        <p className="font-bold text-slate-900">Licensed Digital Bank</p>
                        <p className="text-sm text-slate-500">FDIC Insured</p>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      </Element>

      {/* Services Section */}
      <Element name="services">
        <section className="py-24 bg-slate-50" data-testid="services-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection>
              <motion.div variants={fadeInUp} className="text-center mb-16">
                <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wider">Our Services</span>
                <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mt-4 mb-6">
                  Everything You Need in One Place
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  From international transfers to investment tools, we provide a complete suite of financial services for individuals and businesses.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {services.map((service, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    whileHover={{ y: -8, transition: { duration: 0.3 } }}
                    className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100"
                    data-testid={`service-card-${index}`}
                  >
                    <div className={`w-14 h-14 ${service.color} rounded-xl flex items-center justify-center mb-6`}>
                      <service.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{service.description}</p>
                    <button className="mt-6 text-cyan-600 font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                      Learn More <ArrowRight className="w-4 h-4" />
                    </button>
                  </motion.div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </section>
      </Element>

      {/* Features Section - Bento Grid */}
      <Element name="features">
        <section className="py-24 bg-white" data-testid="features-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection>
              <motion.div variants={fadeInUp} className="text-center mb-16">
                <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wider">Why Choose Us</span>
                <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mt-4 mb-6">
                  {t('whyChooseUs')}
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Experience banking reimagined with features designed for the modern world.
                </p>
              </motion.div>

              {/* Bento Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    variants={scaleIn}
                    whileHover={{ scale: 1.02 }}
                    className={`${
                      index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''
                    } bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-slate-100 hover:border-cyan-200 transition-all duration-300 hover:shadow-lg`}
                    data-testid={`feature-card-${index}`}
                  >
                    <div className="w-12 h-12 bg-[#1a365d]/10 rounded-xl flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-[#1a365d]" />
                    </div>
                    <h3 className={`${index === 0 ? 'text-2xl' : 'text-xl'} font-bold text-slate-900 mb-2`}>
                      {feature.title}
                    </h3>
                    <p className="text-slate-600">{feature.desc}</p>
                    {index === 0 && (
                      <div className="mt-6 grid grid-cols-2 gap-4">
                        <div className="bg-white rounded-xl p-4 shadow-sm">
                          <p className="text-2xl font-bold text-green-600">0.5s</p>
                          <p className="text-sm text-slate-500">Avg. transfer time</p>
                        </div>
                        <div className="bg-white rounded-xl p-4 shadow-sm">
                          <p className="text-2xl font-bold text-[#1a365d]">0%</p>
                          <p className="text-sm text-slate-500">Hidden fees</p>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </section>
      </Element>

      {/* Banking Partners Section */}
      <section className="py-20 bg-white border-y border-slate-100" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wider">Banking Infrastructure</span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-4 mb-4">
                Powered by World-Class Financial Partners
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Our banking operations are supported by leading financial institutions, ensuring secure and reliable international transfers.
              </p>
            </motion.div>

            <motion.div 
              variants={fadeInUp}
              className="flex flex-wrap justify-center items-center gap-12 md:gap-20"
            >
              {/* JP Morgan Chase */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex flex-col items-center"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_finance-hub-613/artifacts/6f65vnay_Chase-Bank-Logo-PNG-Picture.png"
                  alt="JP Morgan Chase"
                  className="h-20 md:h-28 w-auto object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                />
                <span className="text-sm text-slate-500 mt-3">Primary Banking Partner</span>
              </motion.div>

              {/* Sparta Capital */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex flex-col items-center"
              >
                <div className="h-20 md:h-28 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-[#1a365d]">SPARTA</div>
                    <div className="text-lg md:text-xl font-light text-slate-600">CAPITAL</div>
                    <div className="text-xs text-slate-400 tracking-wider">CONSULTING GROUP LLC</div>
                  </div>
                </div>
                <span className="text-sm text-slate-500 mt-3">Master Account Structure</span>
              </motion.div>

              {/* SWIFT */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex flex-col items-center"
              >
                <div className="h-20 md:h-28 flex items-center justify-center">
                  <div className="bg-[#1a365d] text-white px-6 py-4 rounded-lg">
                    <div className="text-2xl md:text-3xl font-bold">SWIFT</div>
                    <div className="text-xs text-center text-white/70">Global Network</div>
                  </div>
                </div>
                <span className="text-sm text-slate-500 mt-3">International Wire Transfers</span>
              </motion.div>
            </motion.div>

            {/* Banking Info */}
            <motion.div 
              variants={fadeInUp}
              className="mt-16 bg-slate-50 rounded-2xl p-8 md:p-12"
            >
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-cyan-600" />
                    Operational Structure
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Youse Capital operates under a <strong>Master Account Structure</strong> through Sparta Capital Consulting Group LLC, connected to JP Morgan Chase banking infrastructure.
                  </p>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Unique Account ID for each client
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Transaction ID (TXID) for every operation
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Full traceability and compliance
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Send className="w-5 h-5 text-cyan-600" />
                    Transfer Methods
                  </h3>
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-slate-200">
                      <div className="font-semibold text-slate-900">International Wire (SWIFT)</div>
                      <div className="text-sm text-slate-500">Settlement: D+0 or D+1 • SWIFT Code: CHASUS33</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-slate-200">
                      <div className="font-semibold text-slate-900">Domestic Transfer (ACH/Wire)</div>
                      <div className="text-sm text-slate-500">Automatic linking to sub-account ID</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* How It Works Section */}
      <Element name="how-it-works">
        <section className="py-24 bg-[#1a365d]" data-testid="how-it-works-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection>
              <motion.div variants={fadeInUp} className="text-center mb-16">
                <span className="text-cyan-400 font-semibold text-sm uppercase tracking-wider">Getting Started</span>
                <h2 className="text-3xl md:text-5xl font-bold text-white mt-4 mb-6">
                  How It Works
                </h2>
                <p className="text-lg text-white/70 max-w-2xl mx-auto">
                  Get started in just 3 simple steps. No paperwork, no waiting.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-3 gap-8">
                {howItWorks.map((item, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="relative"
                  >
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/15 transition-all duration-300">
                      <span className="text-6xl font-bold text-cyan-400/30">{item.step}</span>
                      <h3 className="text-2xl font-bold text-white mt-4 mb-3">{item.title}</h3>
                      <p className="text-white/70">{item.desc}</p>
                    </div>
                    {index < 2 && (
                      <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                        <ArrowRight className="w-8 h-8 text-cyan-400" />
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>

              <motion.div variants={fadeInUp} className="text-center mt-12">
                <RouterLink to={isAuthenticated ? '/dashboard' : '/signup'}>
                  <Button size="lg" className="bg-cyan-500 hover:bg-cyan-400 text-white gap-2 text-lg px-10 py-6 rounded-xl">
                    Start Your Journey
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </RouterLink>
              </motion.div>
            </AnimatedSection>
          </div>
        </section>
      </Element>

      {/* Testimonials Section */}
      <Element name="testimonials">
        <section className="py-24 bg-white" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection>
              <motion.div variants={fadeInUp} className="text-center mb-16">
                <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wider">Testimonials</span>
                <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mt-4 mb-6">
                  Loved by Thousands
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Don't just take our word for it. Here's what our customers have to say.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-3 gap-8">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    whileHover={{ y: -8 }}
                    className="bg-slate-50 rounded-2xl p-8 relative"
                  >
                    <Quote className="absolute top-6 right-6 w-10 h-10 text-cyan-100" />
                    <p className="text-slate-600 mb-6 relative z-10 leading-relaxed">"{testimonial.text}"</p>
                    <div className="flex items-center gap-4">
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name}
                        className="w-14 h-14 rounded-full object-cover"
                      />
                      <div>
                        <p className="font-bold text-slate-900">{testimonial.name}</p>
                        <p className="text-sm text-slate-500">{testimonial.role}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </section>
      </Element>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#1a365d] to-[#0f2744]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <motion.div variants={fadeInUp} className="text-center">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                Ready to Transform Your Banking Experience?
              </h2>
              <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto">
                Join over 2 million customers who trust Youse Capital for their global financial needs.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <RouterLink to={isAuthenticated ? '/dashboard' : '/signup'}>
                  <Button size="lg" className="bg-white text-[#1a365d] hover:bg-white/90 gap-2 text-lg px-10 py-6 rounded-xl">
                    Open Free Account
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </RouterLink>
                <RouterLink to="/login">
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg px-10 py-6 rounded-xl">
                    Sign In
                  </Button>
                </RouterLink>
              </div>
              
              <p className="mt-8 text-white/50 text-sm">
                No credit check required • Free to open • Cancel anytime
              </p>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
